import { useTheme } from 'next-themes';
import { useEffect } from 'react';
import { VelocityTheme } from 'src/environmentVariables/EnvironmentVariableTypes';
import useCurrentSettings from 'src/hooks/useCurrentSettings';

export const ThemeSetter = (): React.ReactNode => {
	const { theme: nextTheme, setTheme } = useTheme();
	const [savedSettings, updateSettings] = useCurrentSettings();
	const { theme } = savedSettings;

	const handleThemeChange = (selectedTheme: 'dark' | 'light') => {
		const velocityTheme =
			selectedTheme === 'dark' ? VelocityTheme.dark : VelocityTheme.light;
		setTheme(velocityTheme);
	};

	const setThemeBasedOnDevice = (): 'dark' | 'light' => {
		const isDark =
			window.matchMedia &&
			window.matchMedia('(prefers-color-scheme: dark)').matches;
		const detectedTheme = isDark ? 'dark' : 'light';
		handleThemeChange(detectedTheme);
		return detectedTheme;
	};

	useEffect(() => {
		if (theme === 'light' && nextTheme === VelocityTheme.light) {
			return;
		}
		if (theme === 'dark' && nextTheme === VelocityTheme.dark) {
			return;
		}
		if (theme === 'dark' || theme === 'light') {
			handleThemeChange(theme);
		}
		if (theme === 'device') {
			// eslint-disable-next-line no-inner-declarations
			function deviceThemeListener(e: MediaQueryListEvent) {
				const matchesTheme = e.matches
					? VelocityTheme.dark
					: VelocityTheme.light;
				setTheme(matchesTheme);
			}
			setThemeBasedOnDevice();

			// Hold onto the MediaQueryList: `matchMedia` returns a new object each
			// call, so removing the listener off a second one would be a no-op.
			const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
			prefersDark.addEventListener('change', deviceThemeListener);

			return () =>
				prefersDark.removeEventListener('change', deviceThemeListener);
		}
		if (theme === null) {
			if (
				nextTheme === null ||
				nextTheme === 'system' ||
				nextTheme === 'device'
			) {
				const deviceTheme = setThemeBasedOnDevice();
				// we need to update user settings based off device preference theme as well
				console.log('setting theme to device theme', deviceTheme);
				updateSettings({ ...savedSettings, theme: 'device' });
			} else if (nextTheme === 'dark-theme' || nextTheme === 'light-theme') {
				handleThemeChange(nextTheme === 'dark-theme' ? 'dark' : 'light');
			}
		}
	}, [theme]);

	useEffect(() => {
		function storageListener(event: StorageEvent) {
			if (event.key === 'theme') {
				handleThemeChange(event.newValue === 'dark-theme' ? 'dark' : 'light');
			}
		}
		window.addEventListener('storage', storageListener);
		return () => window.removeEventListener('storage', storageListener);
	}, []);

	return null;
};
